import theme from '@themes/theme';

export type ColorToken = keyof typeof theme.colors;

/**
 * TA-9005 — Standardized hover styling helpers.
 *
 * Source of truth: `Tasktag_Hover_Spec_v3`. The spec's foundational rule
 * applies to *surfaces* (rows, headers, cards, menu items, ghost/outlined
 * controls): their hover background is one of two tokens only — `grey01`
 * (structural) or `greenSecondaryMild` (navigation). Filled controls and
 * chips follow separate derive-from-resting-colour rules (see
 * `getButtonVariantHoverBackground` / `getChipHoverBackground`), which can
 * yield `grey02`, `textSecondary`, `greenDark`, or an alpha fade. One
 * design-approved surface exception: chat message bubbles hover to `grey02`
 * (see ChatMessage.web). These helpers encode all of these rules so call
 * sites never hardcode a hover colour.
 */

/** The only two background tokens allowed for a *surface* hover state. */
export const HOVER_BG = {
  /**
   * Structural / non-content surfaces: section headers, dropdown triggers and
   * menu items, tabs, cards, search-result rows, chat thread rows, task list
   * rows, member list rows, and the ghost/outlined control family.
   */
  structural: 'grey01',
  /**
   * Navigation surfaces: sidebar nav items — anything that reads as "where am
   * I going", not row content.
   */
  content: 'greenSecondaryMild',
} as const satisfies Record<string, ColorToken>;

/** Background for a *content* row (sidebar nav item). */
export const getContentRowBackground = (
  isHovered: boolean,
  isSelected = false
): ColorToken => {
  if (isSelected || isHovered) return HOVER_BG.content;
  return 'white';
};

/**
 * Background for a *structural* row (section header, search result, chat thread
 * row, dropdown item). Hover tints to grey01; the selected/active state still
 * uses greenSecondaryMild to read as "current".
 */
export const getStructuralRowBackground = (
  isHovered: boolean,
  isSelected = false
): ColorToken => {
  if (isSelected) return HOVER_BG.content;
  return isHovered ? HOVER_BG.structural : 'white';
};

/**
 * Text/icon colour for a row. The spec distinguishes hover from active purely
 * by text colour: hover keeps the neutral colour, active shifts to
 * greenSecondary.
 */
export const getRowForegroundColor = (
  isSelected = false,
  restingColor: ColorToken = 'textPrimary'
): ColorToken => (isSelected ? 'greenSecondary' : restingColor);

/**
 * Hover background for a `Button` variant, derived from that variant's
 * *resting* background so all variants are covered by one rule (TA-9005):
 * solid (black) darkens to textSecondary with white text kept; a solid green
 * CTA darkens to greenDark; already-tinted grey01 controls deepen one step to
 * grey02; white/transparent (ghost/outlined) controls pick up the grey01 tint.
 * Returns `undefined` for bespoke fills (e.g. alertRed) that have no defined
 * auto-hover and are left to Design.
 */
export const getButtonVariantHoverBackground = (
  restingBackground?: ColorToken
): ColorToken | undefined => {
  switch (restingBackground) {
    case 'black':
    // textPrimary (#0A1629) is the near-black fill some CTAs use (e.g. the
    // chat "New Message" button) — same fill family, same grey05 hover.
    case 'textPrimary':
      return 'textSecondary';
    case 'greenSecondary':
      return 'greenDark';
    case 'grey01':
      return 'grey02';
    case 'white':
    case undefined:
      return HOVER_BG.structural;
    default:
      return undefined;
  }
};

/**
 * Hover background for a chip / pill (TA-9005). Per the Figma button sheet
 * ("Outline with bg" row): a neutral chip — transparent, white, or grey01 at
 * rest — deepens to solid grey02 on hover. A colored chip fades its current
 * background to 50% opacity. Returns a CSS color string for `style` usage, or
 * `undefined` when the resting value isn't a plain #RRGGBB hex we can derive
 * an alpha from.
 */
export const getChipHoverBackground = (
  restingBackground?: string
): string | undefined => {
  const resting = restingBackground?.trim().toLowerCase();
  if (
    !resting ||
    resting === 'transparent' ||
    resting === '#ffffff' ||
    resting === theme.colors.grey01.toLowerCase()
  ) {
    return theme.colors.grey02;
  }
  if (/^#[0-9a-f]{6}$/.test(resting)) return `${resting}80`;
  return undefined;
};

export type ControlKind = 'solid' | 'outlined' | 'ghost';

/**
 * Hover background for a button / control by its kind (Interactive Controls
 * spec). Solid controls darken (text stays white); outlined and ghost controls
 * pick up the structural grey01 tint while their border/transparency is kept.
 */
export const getControlHoverBackground = (kind: ControlKind): ColorToken => {
  switch (kind) {
    case 'solid':
      return 'textSecondary';
    case 'outlined':
    case 'ghost':
    default:
      return HOVER_BG.structural;
  }
};
